import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import { USER_MESSAGE_TYPES, USER_MESSAGES, USER_MESSAGES_REVERTED } from '../../shared/UserMessaging';
import { SUBSCRIPTIONS_LINK } from '../../common/urlLinks';
import { PILL_SECONDARY } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    lead: {
        margin: '0 0 6px',
        fontSize: '1rem',
        lineHeight: 1.5,
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.6,
    },
    action: {
        ...PILL_SECONDARY,
        minHeight: '42px',
        marginTop: '14px',
        padding: '0 18px',
        fontSize: '0.9rem',
    },
}));

/** The title of a predefined message, or nothing for unknown types. */
export const messageTitle = (intl, row) =>
    row.messageType === USER_MESSAGE_TYPES.PREDEFINED_TYPE
        ? intl.formatMessage({ id: `messages.USER_MESSAGES.${USER_MESSAGES_REVERTED[row.messageCode]}.title` })
        : '';

/**
 * The body of one message: a lead line, a detail line and, for some codes,
 * the action it invites (the plans page, the hosted invoice or payment page).
 * The copy per code is unchanged; only the frame around it is new.
 */
const MessageBody = ({ row }) => {
    const classes = useStyles();
    const intl = useIntl();

    if (row.messageType !== USER_MESSAGE_TYPES.PREDEFINED_TYPE) return null;

    const meta = row.meta || {};
    const values = {
        date: meta.endDate
            ? intl.formatDate(new Date(meta.endDate), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            : '',
        subscriptionName: meta.subscriptionName || 'N/A',
        hostedUrl: meta.hostedUrl || '#',
        hostedPdf: meta.hostedPdf || '#',
    };
    const code = USER_MESSAGES_REVERTED[row.messageCode];
    const line = (part) => (
        <FormattedMessage id={`messages.USER_MESSAGES.${code}.${part}`} values={values} />
    );

    switch (row.messageCode) {
        case USER_MESSAGES.UPGRADED_TO_BONUS_TRIAL:
        case USER_MESSAGES.UPDATED_SUBSCRIPTION:
        case USER_MESSAGES.CANCELED_SUBSCRIPTION:
            return (
                <>
                    <p className={classes.lead}>{line('b1')}</p>
                    <p className={classes.text}>{line('b2')}</p>
                </>
            );
        case USER_MESSAGES.STARTED_FREE:
            return (
                <>
                    <p className={classes.lead}>{line('b1')}</p>
                    <p className={classes.text}>{line('b2')}</p>
                    <Link to={SUBSCRIPTIONS_LINK} className={classes.action}>
                        <FormattedMessage id="topMenu.subscriptions.name" />
                        <span aria-hidden="true">→</span>
                    </Link>
                </>
            );
        case USER_MESSAGES.INVOICE_GENERATED:
        case USER_MESSAGES.PAYMENT_FAILED:
            return (
                <>
                    <p className={classes.lead}>{line('b1')}</p>
                    <a href={values.hostedUrl} target="_blank" rel="noreferrer" className={classes.action}>
                        {line('b2')}
                        <span aria-hidden="true">→</span>
                    </a>
                </>
            );
        default:
            return <p className={classes.text}>N/A</p>;
    }
};

export default MessageBody;
